import { onAnimationEnd } from '@theme/utilities';

/**
 * ECS (GLOSS-72): the FILTER/SORT bar's SORT trigger opens the same facets
 * drawer as FILTER (sorting lives inside it), but per design QA it should
 * land the shopper on sorting, not the filter list. The trigger carries
 * `data-facets-sort-shortcut`; when it opens the drawer, the drawer's Sort
 * accordion is expanded, scrolled into view, and given focus once the
 * drawer's opening animation settles (the theme's own animation-end
 * utility, so the timing tracks the CSS transition).
 *
 * Capture-phase listener so it works regardless of the theme's own on:click
 * handling order; it never opens or closes the drawer itself — that stays
 * the drawer component's job.
 */
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-facets-sort-shortcut]');
    if (!trigger) return;

    requestAnimationFrame(() => {
      const drawer = document.getElementById('filters-drawer');
      if (!drawer) return;
      const panel = drawer.querySelector('dialog') || drawer;

      onAnimationEnd(panel, () => {
        // The drawer renders two sort presentations: a details accordion
        // (desktop) and a native select row (mobile) — the other one is
        // hidden. Pin the component root, which is visible either way, and
        // only expand/focus the accordion when it is the visible variant.
        const component = drawer.querySelector('sorting-filter-component');
        const sorting = drawer.querySelector('details[id^="Sorting-"]');
        const target = component || sorting;
        if (!(target instanceof HTMLElement)) return;

        if (sorting instanceof HTMLDetailsElement && sorting.offsetParent !== null) {
          sorting.open = true;
          const summary = sorting.querySelector('summary');
          if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
        }

        // The drawer's filter groups and the sort accordion keep animating
        // (grid-template-rows transitions) after the panel itself lands, so a
        // one-shot scroll gets pushed back out of view. Re-pin the group each
        // frame until its position holds still, with a hard time cap.
        // The loop can't tell layout settling from the shopper scrolling, so
        // any manual input hands control back immediately.
        const deadline = performance.now() + 1200;
        let userTookOver = false;
        const takeOver = () => {
          userTookOver = true;
        };
        const inputEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
        for (const name of inputEvents) document.addEventListener(name, takeOver, { capture: true, once: true, passive: true });
        const cleanup = () => {
          for (const name of inputEvents) document.removeEventListener(name, takeOver, { capture: true });
        };
        let lastTop = Infinity;
        let stableFrames = 0;
        const pin = () => {
          if (userTookOver) return cleanup();
          const top = target.getBoundingClientRect().top;
          if (top !== lastTop) {
            target.scrollIntoView({ block: 'start' });
            lastTop = target.getBoundingClientRect().top;
            stableFrames = 0;
          } else {
            stableFrames += 1;
          }
          if (stableFrames < 5 && performance.now() < deadline) return requestAnimationFrame(pin);
          cleanup();
        };
        requestAnimationFrame(pin);
      });
    });
  },
  true
);
